//  ----------      START   IMPORT  BLOCK       ----------
import React, { 
    createContext, 
    useContext, 
    useState, 
    useMemo, 
    useEffect, 
    useCallback, 
} from 'react';
import type { ReactNode } from 'react';
//  Context
import { useUser } from './UserContext';
import { UserRole } from '../../Types/User/UserRoles';
//  ----------      END   IMPORT  BLOCK       ----------

//  Define what this context provides to the rest of the app
interface AppUIContextType {
    isProfessionalDashboardActive: boolean;
    toggleProfessionalDashboard: (newState?: boolean) => void;
}

const ApplicationUIContext = createContext<AppUIContextType | null>(null);

/**
 * Hook to access the Application UI context value.
 */
export const useApplicationUI = (): AppUIContextType => {
    const context = useContext(ApplicationUIContext);
    if (!context) {
        throw new Error('useApplicationUI must be used within an ApplicationUIProvider');
    }
    return context;
};

/**
 * Custom hook to easily access the user's current preferred dashboard view
 */
export const useDashboardPreference = () => {
    const { isProfessionalDashboardActive, toggleProfessionalDashboard } = useApplicationUI(); 
    return { 
        isProDashboardActive: isProfessionalDashboardActive, 
        togglePreference: toggleProfessionalDashboard 
    };
}

interface ProviderProps {
    children: ReactNode;
}

/**
 * Provides application-wide UI state, such as dashboard preferences.
 */
export const ApplicationUIProvider = ({ children }: ProviderProps) => {
    const { 
        user, 
        hasRole, 
        isMedicIntent, 
        isMedicProfileComplete 
    } = useUser();
    
    const [isProfessionalDashboardActive, setIsProfessionalDashboardActive] = useState<boolean>(false);

    /**
     * Toggles the dashboard view. 
     * Accepts an optional boolean to force a state, or toggles current state if empty.
     */
    const toggleProfessionalDashboard = useCallback((newState?: boolean) => {
        // 🎯 Sturdy check: If they aren't a medic, they can't be in the pro dashboard
        if (!isMedicIntent) {
            setIsProfessionalDashboardActive(false);
            return;
        }

        if (typeof newState === 'boolean') {
            setIsProfessionalDashboardActive(newState);
        } else {
            setIsProfessionalDashboardActive(prev => !prev);
        }
    }, [isMedicIntent]);

    // UI Logic: Auto-switch dashboard based on User State
    useEffect(() => {
        if (!user) return;

        // Force Pro view if:
        // 1. Medic profile is incomplete (Pending Pro)
        // 2. Medic profile is complete but they haven't set up the patient side yet (Verifying/Pro)
        const shouldDefaultToPro = isMedicIntent && (!isMedicProfileComplete || !hasRole(UserRole.BASE));

        // Only force the Professional view if they are a doctor who has NOT completed onboarding.
        // If they are fully onboarded, do not force them; let the user's selection stand.
        const mustBeInProOnboarding = isMedicIntent && !isMedicProfileComplete;

        if (mustBeInProOnboarding && !isProfessionalDashboardActive) {
        setIsProfessionalDashboardActive(true);
    }
}, [user, isMedicIntent, isMedicProfileComplete, isProfessionalDashboardActive]);

    const value = useMemo(() => ({
        isProfessionalDashboardActive, 
        toggleProfessionalDashboard,
    }), [isProfessionalDashboardActive, toggleProfessionalDashboard]);

    return (
        <ApplicationUIContext.Provider value={value}>
            {children}
        </ApplicationUIContext.Provider>
    );
};